<!-- src/theme/default/views/banking/risk/interest-rate/GeneralAssumptions.vue -->
<template>
  <v-row class="ma-0">
    <v-col
      cols="12"
      class="py-3"
    >
      <div class="assumption-card">
        <div class="assumption-title d-flex align-center">
          <span>{{ $t('Assumptions.EditAssumptions.Tabs.LiquidityAssumptions.Title') }}</span>
        </div>

        <!-- fields in two columns -->
        <v-row class="ma-0">
          <v-col
            v-for="([k, v], index) in Object.entries(model)"
            :key="k"
            :model="v"
            cols="12"
            md="6"
            :class="`pa-0 ${index % 2 === 0 ? 'pr-md-10' : ''} py-3`"
          >
            <!-- label -->
            <div class="d-flex flex-row align-center justify-space-between">
              <div class="row-title">
                {{ v.title }}
              </div>

              <!-- numeric input -->
              <v-text-field
                :value="formatDisplayValue(k)"
                type="text"
                hide-details
                dense
                outlined
                class="pct-input"
                :prefix="v.type === 'AMOUNT' ? baseCurrency : ''"
                :suffix="v.type === 'PERCENTAGE' ? '%' : ''"
                style="background:white;border:1px solid var(--v-synthesis-ui-grey-02-base);font-weight:400;"
                @input="onInput(k, $event)"
                @blur="onBlur(k)"
                @keydown="blockInvalidKeys($event, range(k).min)"
              />
            </div>

            <!-- slider ONLY for percentage fields -->
            <v-slider
              v-if="v.type === 'PERCENTAGE'"
              :value="safeValue(k)"
              :min="range(k).min"
              :max="range(k).max"
              :track-color="getSliderTrackColor(safeValue(k))"
              hide-details
              class="flex-grow-1 slider-input"
              :style="sliderStyle()"
              @input="onInput(k, $event)"
            />
          </v-col>
        </v-row>
      </div>
    </v-col>
  </v-row>
</template>

<script>
import chartsColorMixin from '@/theme/default/components/charts/mixins/chartsColorMixin'

export default {
  name  : 'LiquidityAssumptions',
  mixins: [chartsColorMixin],
  props : {
    baseCurrency: { type: String, required: false, default: '' },
    /** model: { [fieldCode]: { type, value, label } } */
    model       : { type: Object, required: true },
    /**
     * Optional ranges for percentage sliders:
     * ranges = { [fieldCode]: { { min:number, max:number } } }
     * Defaults to 0..100 when not provided.
     */
    ranges      : { type: Object, default: () => ({}) }
  },
  methods: {
    blockInvalidKeys (e, min) {
      const key = e.key
      const disallowMinus = min >= 0

      // Allow Ctrl/Cmd combinations (copy, paste, etc.) - check this first
      if (e.ctrlKey || e.metaKey) {
        return // Allow Ctrl/Cmd combinations
      }

      // Allow: backspace, delete, tab, escape, enter, and navigation keys
      const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End']

      // Allow special keys
      if (allowedKeys.includes(key)) {
        return // Allow the key
      }

      // Allow numbers (0-9)
      if (key >= '0' && key <= '9') {
        return // Allow the key
      }

      // Allow decimal point (but check for percentage fields - they need decimals)
      if (key === '.') {
        return // Allow decimal point
      }

      // Allow comma (for formatting, though we'll parse it out)
      if (key === ',') {
        return // Allow but we'll remove it during parsing
      }

      // Allow minus sign only if negative values are allowed
      if (key === '-' && !disallowMinus) {
        return // Allow minus if negative range is allowed
      }

      // Block everything else (letters, special chars, etc.)
      e.preventDefault()
    },
    isNegativeRange (minValue) {
      return minValue < 0
    },
    getSliderTrackColor (minValue) {
      return this.isNegativeRange(minValue) ? this.themeColorRGBA('synthesis-ui-blue-04') : this.themeColorRGBA('synthesis-ui-grey-04')
    },
    safeValue (fieldCode) {
      const v = this.model?.[fieldCode]?.value
      return Number.isFinite(Number(v)) ? Number(v) : 0
    },
    formatDisplayValue (fieldCode) {
      const value = this.safeValue(fieldCode)
      if (!Number.isFinite(value)) return ''

      // Get field type to determine decimal places
      const field = this.model?.[fieldCode]
      const isPercentage = field?.fieldType === 'PERCENTAGE'

      // Format with thousands separators
      return value.toLocaleString('en-US', {
        minimumFractionDigits: isPercentage ? 2 : 0,
        maximumFractionDigits: isPercentage ? 2 : 0
      })
    },
    parseInputValue (rawValue) {
      // Remove commas and any non-numeric characters except decimal point and minus
      if (!rawValue) return 0

      // Remove commas
      const cleaned = rawValue.toString().replace(/,/g, '')

      // Parse as number
      const parsed = parseFloat(cleaned)
      return Number.isFinite(parsed) ? parsed : 0
    },
    onInput (fieldCode, raw) {
      const value = this.parseInputValue(raw)
      this.$emit('update-liquidity', { fieldCode, value })
    },
    onBlur (fieldCode) {
      // Ensure value is formatted on blur
      const value = this.safeValue(fieldCode)
      // The formatDisplayValue will handle the formatting automatically
      // This handler ensures the value is properly set if empty
      if (!Number.isFinite(value) || value === 0) {
        // Optionally set to 0 if empty, or leave as is
        // The parent component should handle validation
      }
    },
    range (fieldCode) {
      const r = this.ranges?.[fieldCode]
      return {
        min: Number.isFinite(r?.min) ? Number(r.min) : 0,
        max: Number.isFinite(r?.max) ? Number(r.max) : 100
      }
    },
    // Simple, tasteful slider styling (matches your previous look)
    sliderStyle () {
      const c = '#3D68D5' // primary track/thumb
      const glow = 'rgba(61,104,213,.18)'
      const thumbGlow = 'rgba(61,104,213,.32)'
      return {
        '--c'          : c,
        '--glow'       : glow,
        '--thumb-color': c,
        '--thumb-glow' : thumbGlow
      }
    }
  }
}
</script>

<style scoped>
.assumption-card{
  border: 1px solid #E6EEF5;
  background: #FFFFFF;
  border-radius: 12px;
  padding: 16px;
}
.assumption-title{
  font-size: 14px;
  font-weight: 600;
  color: #63747D;
  margin-bottom: 10px;
}
.row-title{
  font-size: 13px;
  font-weight: 600;
  color: #041F30;
  margin-bottom: 4px;
}
.pct-input{
  max-width: 220px;
  input { color: var(--v-synthesis-ui-grey-01-base); }
}

/* Hide number input spinners (Chrome) */
::v-deep .pct-input input[type="number"]::-webkit-outer-spin-button,
::v-deep .pct-input input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  appearance: none;
  margin: 0;
}
/* Hide number spinners (Firefox) */
::v-deep .pct-input input[type="number"] {
  -moz-appearance: textfield;
  appearance: textfield;
}
/* Suffix/prefix visual parity */
::v-deep .pct-input .v-input__prepend-inner,
::v-deep .pct-input .v-input__append-inner,
::v-deep .pct-input .v-text-field__suffix,
::v-deep .pct-input .v-text-field__prefix {
  color: var(--v-synthesis-ui-grey-01-base) !important;
}

/* ===== Slider polish (same visual language as your other sliders) ===== */
::v-deep .slider-input .v-slider__track-background,
::v-deep .slider-input .v-slider__track-fill {
  height: 10px;
  border-radius: 6px;
}
::v-deep .slider-input .v-slider__track-background {
  border: 1px solid #DDE6F1;
  box-shadow: inset 0 1px 0 rgba(0,0,0,0.02);
}
::v-deep .slider-input .v-slider__track-fill {
  border-radius: 2px;
  height: 10px;
  background-color: var(--c, #3D68D5) !important;
  box-shadow: 0 0 0 0 var(--glow, transparent);
}
::v-deep .slider-input .v-slider__thumb {
  margin-top: 4px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 1px solid #FFFFFF !important;
  background-color: var(--thumb-color, #3D68D5) !important;
  box-shadow: 0 0 8px 0 var(--thumb-glow, rgba(61,104,213,.32)) !important;
}
::v-deep .slider-input .v-slider__thumb::before { display: none; }
::v-deep .slider-input .v-input__control,
::v-deep .slider-input .v-input__slot { margin: 0; }

/* Disabled/readonly input styles */
::v-deep .disabled-input {
  opacity: 0.6;
  background-color: #f5f5f5 !important;
}
::v-deep .disabled-input input {
  background-color: #f5f5f5 !important;
  color: #9e9e9e !important;
  cursor: not-allowed;
}
::v-deep .disabled-input fieldset {
  border-color: #e0e0e0 !important;
}

/* Disabled slider styles */
::v-deep .disabled-slider {
  opacity: 0.6;
  pointer-events: none;
}
::v-deep .disabled-slider .v-slider__track-background,
::v-deep .disabled-slider .v-slider__track-fill {
  background-color: #e0e0e0 !important;
}
::v-deep .disabled-slider .v-slider__thumb {
  background-color: #bdbdbd !important;
  box-shadow: none !important;
}
</style>
